import React, { useState, useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import CalendarMini from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "./calendar.css";
import PopupSlot from "../popupSlot/popupSlot";

const START_HOUR = 7;
const END_HOUR = 19;
const SLOT_INTERVAL = 60;

// 🎨 Couleurs cycliques pour les RDV (1er = rouge, 2e = vert, 3e = bleu…)
const RDV_COLORS = [
  "#ef4444", // rouge
  "#22c55e", // vert
  "#3b82f6", // bleu
  "#f59e0b", // orange
  "#a855f7", // violet
  "#06b6d4", // cyan
  "#f97316", // orange foncé
];

/* ===== API BASE (prod = même domaine ; sinon ENV) ===== */
const RAW =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) ||
  process.env.REACT_APP_API_URL ||
  process.env.VITE_API_URL ||
  "";
const API_BASE = RAW
  ? RAW.replace(/\/+$/, "").replace(/\/api$/i, "")
  : (typeof window !== "undefined" && window.location.origin) || "";

// =================== Helpers sûrs ===================
const safeSplit = (str, sep) =>
  typeof str === "string" ? str.split(sep) : [];

const safeParseInt = (v, def = 0) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : def;
};

const buildDateFromDateHeure = (dateStr, heureStr) => {
  // Attend "DD/MM/YYYY" et "HH:MM" — retourne null si invalide
  if (
    typeof dateStr === "string" &&
    dateStr.includes("/") &&
    typeof heureStr === "string" &&
    heureStr.includes(":")
  ) {
    const [d, m, y] = safeSplit(dateStr, "/").map((x) => safeParseInt(x));
    const [hh, mm] = safeSplit(heureStr, ":").map((x) => safeParseInt(x));
    if (y && m && d) {
      return new Date(y, m - 1, d, hh || 0, mm || 0, 0, 0);
    }
  }
  return null;
};

// === Helpers horaires (profil) ===
const normalizeWorkDay = (dayObj) => {
  if (!dayObj || dayObj.isOpen === false) return { isOpen: false, slots: [] };
  if (Array.isArray(dayObj.slots) && dayObj.slots.length > 0) {
    const slots = dayObj.slots.filter((s) => s && s.start && s.end);
    return { isOpen: true, slots };
  }
  if (dayObj.start || dayObj.end) {
    return {
      isOpen: true,
      slots: [{ start: dayObj.start || "00:00", end: dayObj.end || "23:59" }],
    };
  }
  return { isOpen: false, slots: [] };
};

const toDateAtHHMM = (baseDate, hhmm) => {
  if (typeof hhmm !== "string" || !hhmm.includes(":")) return null;
  const [h, m] = safeSplit(hhmm, ":").map((x) => safeParseInt(x));
  const d = new Date(baseDate);
  d.setHours(h, m, 0, 0);
  return d;
};

const isInsideWorkHours = (dt, workHours) => {
  // dt: Date (début du créneau)
  // workHours: tableau de jours profil { day, isOpen, slots[] } ou {start/end}
  if (!dt || !Array.isArray(workHours) || workHours.length === 0) return true; // si pas d'info -> ne pas bloquer
  const jourFR = dt.toLocaleDateString("fr-FR", { weekday: "long" }).toLowerCase();
  const rawDay = workHours.find((wh) => (wh?.day || "").toLowerCase().startsWith(jourFR));
  const { isOpen, slots } = normalizeWorkDay(rawDay);
  if (!isOpen || slots.length === 0) return false;

  // on vérifie que [dt, dt+SLOT_INTERVAL] tient dans au moins une plage
  const end = new Date(dt.getTime() + SLOT_INTERVAL * 60000);
  return slots.some((seg) => {
    const segStart = toDateAtHHMM(dt, seg?.start);
    const segEnd = toDateAtHHMM(dt, seg?.end);
    if (!segStart || !segEnd) return false;
    return dt >= segStart && end <= segEnd;
  });
};

// ====================================================

const daysLabels = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const Calendar = () => {
  const isNativeApp = Capacitor.isNativePlatform();

  const selectedProfilId = localStorage.getItem("selectedProfilId") || "";
  const userId = localStorage.getItem("userId");
  const token = localStorage.getItem("token");

  const [slots, setSlots] = useState([]); // RDVs + slots pro (hors congés)
  const [conges, setConges] = useState([]); // congés/indispos (BLOCK)
  const [currentDate, setCurrentDate] = useState(new Date());
  const [popupVisible, setPopupVisible] = useState(false);
  const [selectedSlotTime, setSelectedSlotTime] = useState(null);
  const [annonceId, setAnnonceId] = useState(null);
  const [profils, setProfils] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [popupSlotDetails, setPopupSlotDetails] = useState(null);

  // Palette RDV calculée (id -> couleur)
  const [rdvColors, setRdvColors] = useState({});

  // Modes congés
  const [isCongeMode, setIsCongeMode] = useState(false);
  const [isCongeRangeMode, setIsCongeRangeMode] = useState(false);
  const [miniRange, setMiniRange] = useState(null);

  // Popup congé (création/édition)
  const [popupCongeVisible, setPopupCongeVisible] = useState(false);
  const [congeDraft, setCongeDraft] = useState({
    _id: null,
    allDay: true,
    start: null,
    end: null,
    duration: 60,
    reason: "",
  });

  // Horaires de travail du profil sélectionné
  const [workHours, setWorkHours] = useState([]);

  // Pour la version App : vue JOUR avec onglets
  const [appSelectedDayIdx, setAppSelectedDayIdx] = useState(0); // 0 = Lundi
  useEffect(() => {
    if (!isNativeApp) return;
    const prev = document.body.style.paddingBottom;
    document.body.style.paddingBottom = "88px";
    return () => { document.body.style.paddingBottom = prev; };
  }, [isNativeApp]);

  // ------- Chargement annonce/profils -------
  useEffect(() => {
    if (!userId) return;
    fetch(`${API_BASE}/api/annonces/by-user/${userId}`, {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        const a = Array.isArray(data) ? data[0] : data;
        if (!a || !a._id) return;
        setAnnonceId(a._id);
        setProfils(Array.isArray(a.profils) ? a.profils : []);
        // Si un profil est sélectionné, récupère aussi ses horaires
        if (selectedProfilId && Array.isArray(a.profils)) {
          const p =
            a.profils.find(
              (x) => x?._id === selectedProfilId || x?._id?.toString() === selectedProfilId
            ) || null;
          if (p?.workingHours) setWorkHours(p.workingHours);
          else setWorkHours([]);
        } else {
          setWorkHours([]);
        }
      })
      .catch(() => {
        setAnnonceId(null);
        setWorkHours([]);
      });
  }, [userId, token, selectedProfilId]);

  // ------- Chargement slots & congés -------
  useEffect(() => {
    fetchSlots();
    fetchConges();
    // eslint-disable-next-line
  }, [currentDate, selectedProfilId]);

  const fetchSlots = async () => {
    if (!userId) return;
    try {
      let url = `${API_BASE}/api/slotsCalendar/pro/${userId}`;
      if (selectedProfilId) url += `?profilId=${selectedProfilId}`;

      const response = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
      });
      let proSlots = await response.json().catch(() => []);
      if (!Array.isArray(proSlots)) proSlots = [];

      // ❗️On enlève les congés (BLOCK) du tableau “slots” (ils sont gérés à part)
      proSlots = proSlots.filter((s) => s.type !== "BLOCK");

      // RDV -> events
      let rdvsAsSlots = [];
      if (selectedProfilId) {
        try {
          const rdvRes = await fetch(
            `${API_BASE}/api/rdvs/profil/${selectedProfilId}`,
            {
              headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              credentials: "include",
            }
          );
          if (rdvRes.ok) {
            const rdvs = await rdvRes.json().catch(() => []);
            if (Array.isArray(rdvs)) {
              rdvsAsSlots = rdvs.map((rdv) => {
                let start = buildDateFromDateHeure(rdv?.date, rdv?.heure);
                if (!start && rdv?.start) start = new Date(rdv.start);
                if (!start) start = new Date();
                const end =
                  rdv?.end ? new Date(rdv.end) : new Date(start.getTime() + 30 * 60000);

                let description = "";
                if (rdv?.clientId) {
                  description += `Client : ${rdv.clientId.prenom || ""} ${rdv.clientId.nom || ""}`;
                  if (rdv.clientId.email) description += `\nEmail : ${rdv.clientId.email}`;
                  if (rdv.clientId.telephone) description += `\nTel : ${rdv.clientId.telephone}`;
                }
                if (rdv?.activity) description += `\nActivité : ${rdv.activity}`;

                return {
                  _id: rdv._id,
                  start,
                  end,
                  title: rdv.activity || "RDV réservé",
                  description,
                  isRdv: true,
                };
              });
            }
          }
        } catch {
          // ignore
        }
      }

      const merged = [...proSlots, ...rdvsAsSlots];
      setSlots(merged);
    } catch (err) {
      console.error("Erreur lors du chargement des créneaux :", err);
      setSlots([]);
    }
  };

  const fetchConges = async () => {
    if (!userId) return;
    try {
      let url = `${API_BASE}/api/slotsCalendar/pro/${userId}`;
      if (selectedProfilId) url += `?profilId=${selectedProfilId}`;
      const res = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
      });
      let data = await res.json().catch(() => []);
      if (!Array.isArray(data)) data = [];

      const blocks = data
        .filter((s) => s.type === "BLOCK")
        .map((c) => ({
          ...c,
          _id: c._id || c.id,
          start: new Date(c.start),
          end: new Date(c.end),
          title: "Congé",
          isConge: true,
        }));

      setConges(blocks);
    } catch {
      console.warn("GET /api/slotsCalendar/pro/... échec, congés non chargés.");
      setConges([]);
    }
  };

  // ➕ Attribution des couleurs aux RDV (ordre chronologique, RDV à venir)
  useEffect(() => {
    const now = new Date();
    const rdvs = (slots || [])
      .filter((s) => s.isRdv)
      .sort((a, b) => new Date(a.start) - new Date(b.start));

    const map = {};
    let idx = 0;
    rdvs.forEach((ev) => {
      const s = new Date(ev.start);
      if (s >= now) {
        const key = ev._id || ev.id || (s.toISOString() + (ev.title || ""));
        map[key] = RDV_COLORS[idx % RDV_COLORS.length];
        idx++;
      }
    });
    setRdvColors(map);
  }, [slots]);

  // ------- Sauvegarde de créneau standard -------
  const saveSlot = async ({
    title,
    description,
    duration,
    emailUser,
    startDate,
    annonceId: annonceIdFromPopup,
    profilId: profilIdFromPopup,
  }) => {
    const proId = userId;
    const useProfilId =
      profilIdFromPopup ||
      selectedProfilId ||
      localStorage.getItem("selectedProfilId");
    const start = startDate ? new Date(startDate) : selectedSlotTime || new Date();
    const end = new Date(start.getTime() + (Number(duration) || 60) * 60000);

    // Bloquer si en dehors des heures du profil
    if (!isInsideWorkHours(start, workHours)) {
      alert("❌ Ce créneau n'est pas dans les horaires d'ouverture du profil.");
      return;
    }

    try {
      const slotRes = await fetch(`${API_BASE}/api/slotsCalendar`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({
          start,
          end,
          title,
          description,
          emailUser,
          proId,
          profilId: useProfilId || null,
          annonceId: annonceIdFromPopup || annonceId || null,
        }),
      });

      if (!slotRes.ok) {
        const txt = await slotRes.text();
        alert("Erreur lors de l'enregistrement du créneau.\n" + txt);
        return;
      }
      setPopupVisible(false);
      fetchSlots();
    } catch (error) {
      alert("Erreur réseau ou serveur.");
    }
  };

  // ------- CONGÉS : création/édition/suppression -------
  const openCongePopupAt = (dateClicked) => {
    setCongeDraft({
      _id: null,
      allDay: false,
      start: dateClicked,
      end: null,
      duration: 60,
      reason: "",
    });
    setPopupCongeVisible(true);
  };

  const handleMiniCalendarChange = (value) => {
    if (isCongeRangeMode && Array.isArray(value)) {
      const [s, e] = value;
      if (s && e) {
        const start = new Date(s);
        start.setHours(0, 0, 0, 0);
        const end = new Date(e);
        end.setHours(23, 59, 59, 999);
        setMiniRange([start, end]);
        setCongeDraft({
          _id: null,
          allDay: true,
          start,
          end,
          duration: 60,
          reason: "",
        });
        setPopupCongeVisible(true);
      }
      return;
    }

    setSelectedDate(value);
    const monday = getMonday(value);
    setCurrentDate(monday);
    if (isNativeApp) {
      const idx = (new Date(value).getDay() + 6) % 7; // 0->Lun
      setAppSelectedDayIdx(idx);
    }
  };

  const saveConge = async () => {
    try {
      let { _id, allDay, start, end, duration, reason } = congeDraft;
      start = start ? new Date(start) : new Date();

      if (!end) {
        if (allDay) {
          const d = new Date(start);
          d.setHours(23, 59, 59, 999);
          end = d;
        } else {
          end = new Date(start.getTime() + (duration || 60) * 60000);
        }
      }

      const payload = {
        proId: userId,
        profilId: selectedProfilId || null,
        start,
        end,
        allDay: !!allDay,
        reason: reason || "",
      };

      const url = _id
        ? `${API_BASE}/api/slotsCalendar/blocks/${_id}`
        : `${API_BASE}/api/slotsCalendar/blocks`;
      const method = _id ? "PATCH" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const txt = await res.text();
        alert("Erreur lors de l'enregistrement du congé.\n" + txt);
        return;
      }

      setPopupCongeVisible(false);
      setIsCongeRangeMode(false);
      setMiniRange(null);
      await fetchConges();
      await fetchSlots();
    } catch {
      alert("Erreur réseau/serveur (POST/PATCH /api/slotsCalendar/blocks).");
    }
  };

  const deleteConge = async (id) => {
    try {
      const res = await fetch(
        `${API_BASE}/api/slotsCalendar/blocks/${id}?proId=${userId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: "include",
        }
      );
      if (!res.ok) {
        const txt = await res.text();
        alert("Erreur suppression congé.\n" + txt);
      }
      setPopupSlotDetails(null);
      await fetchConges();
      await fetchSlots();
    } catch {
      alert("Erreur réseau/serveur (DELETE /api/slotsCalendar/blocks/:id).");
    }
  };

  const deleteSlot = async (id) => {
    if (!userId) return console.error("Aucun userId trouvé");
    if (popupSlotDetails?.isConge) {
      await deleteConge(id);
      return;
    }
    await fetch(`${API_BASE}/api/slotsCalendar/${id}?userId=${userId}`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ userId }),
    });
    setPopupSlotDetails(null);
    fetchSlots();
  };

  // ------- Navigation semaine -------
  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + direction * 7);
    setCurrentDate(newDate);
    setSelectedDate(newDate);
  };

  const handleSelection = (day, hour, minute) => {
    if (!annonceId) {
      alert("Patiente, chargement des données...");
      return;
    }
    const firstDayOfWeek = getMonday(currentDate);
    const dateClicked = new Date(firstDayOfWeek);
    dateClicked.setDate(firstDayOfWeek.getDate() + day);
    dateClicked.setHours(hour, minute, 0, 0);

    // Si créneau hors horaires -> inactif
    if (!isInsideWorkHours(dateClicked, workHours)) {
      return; // pas de popup
    }

    if (isCongeMode) {
      openCongePopupAt(dateClicked);
    } else {
      setSelectedSlotTime(dateClicked);
      setPopupVisible(true);
    }
  };

  const handleSlotClick = (slot) => {
    setPopupSlotDetails(slot);
  };

  const getMonday = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  // Helper d’affichage "Mois Année" (fr) avec 1re lettre en majuscule
  const formatMonthYear = (d) => {
    const s = d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  // ---------- Rendu des horaires sous le mini-calendrier ----------
  const renderWorkHoursSummary = () => {
    const order = ["lundi","mardi","mercredi","jeudi","vendredi","samedi","dimanche"];
    const normalize = (s) => String(s || "").toLowerCase().trim();
    const map = new Map();
    (workHours || []).forEach((wh) => {
      const key = order.find((d) => normalize(wh.day || "").startsWith(d)) || normalize(wh.day || "");
      map.set(key, normalizeWorkDay(wh));
    });

    const fmtSlots = (entry) => {
      if (!entry?.isOpen) return <span className="wh-closed">Fermé</span>;
      if (!entry?.slots || entry.slots.length === 0) return <span>Fermé</span>;
      return (
        <span>
          {entry.slots.map((s, i) => (
            <span key={i} className="wh-slot">
              {s.start}–{s.end}{i < entry.slots.length - 1 ? " · " : ""}
            </span>
          ))}
        </span>
      );
    };

    return (
      <div className="workhours-summary">
        <div className="wh-title">Horaires du profil</div>
        <div className="wh-list">
          {order.map((d) => {
            const e = map.get(d);
            return (
              <div key={d} className="wh-row">
                <div className="wh-day">{d.charAt(0).toUpperCase() + d.slice(1)}</div>
                <div className="wh-hours">{fmtSlots(e)}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ------- Rendu entêtes jours (web) -------
  const renderDays = () => {
    const firstDayOfWeek = getMonday(currentDate);
    const today = new Date();
    return daysLabels.map((day, i) => {
      const dayDate = new Date(firstDayOfWeek);
      dayDate.setDate(firstDayOfWeek.getDate() + i);
      const isToday =
        dayDate.getDate() === today.getDate() &&
        dayDate.getMonth() === today.getMonth() &&
        dayDate.getFullYear() === today.getFullYear();

      // Jour ouvert/fermé (pour style header)
      const rawDay = (workHours || []).find((wh) =>
        (wh?.day || "").toLowerCase().startsWith(
          dayDate.toLocaleDateString("fr-FR", { weekday: "long" }).toLowerCase()
        )
      );
      const { isOpen } = normalizeWorkDay(rawDay);

      return (
        <div key={i} className={`day-header${isToday ? " today" : ""} ${isOpen ? "" : "day-closed"}`}>
          {day}{" "}
          {dayDate.toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
          })}
        </div>
      );
    });
  };

  // ------- Rendu grille semaine (web) -------
  const renderSlots = () => {
    const slotElements = [];
    const covered = {};
    const firstDayOfWeek = getMonday(currentDate);

    const events = [...slots.map((s) => ({ ...s, isConge: false })), ...conges];

    const getKey = (ev) => ev._id || ev.id || (new Date(ev.start).toISOString() + (ev.title || ""));

    for (let day = 0; day < 7; day++) {
      const dayStart = new Date(firstDayOfWeek);
      dayStart.setDate(firstDayOfWeek.getDate() + day);
      dayStart.setHours(START_HOUR, 0, 0, 0);

      const dayEnd = new Date(dayStart);
      dayEnd.setHours(END_HOUR + 1, 0, 0, 0); // fin exclusive

      for (let hour = START_HOUR; hour <= END_HOUR; hour++) {
        const row = hour - START_HOUR + 1;
        const key = `${day}-${row}`;
        if (covered[key]) continue;

        const cellStart = new Date(dayStart);
        cellStart.setHours(hour, 0, 0, 0);
        const cellEnd = new Date(cellStart.getTime() + SLOT_INTERVAL * 60000);
        const isFirstHour = hour === START_HOUR;

        const ev = events.find((e) => {
          const s = new Date(e.start);
          const ed = new Date(e.end);
          const overlaps = s < cellEnd && ed > cellStart;
          if (!overlaps) return false;
          if (s < dayStart) return isFirstHour;
          return s >= cellStart && s < cellEnd;
        });

        if (ev) {
          const s = new Date(ev.start);
          const ed = new Date(ev.end);
          const visibleStart = s < dayStart ? dayStart : s < cellStart ? cellStart : s;
          const visibleEnd = ed > dayEnd ? dayEnd : ed;

          const rowsToSpan = Math.max(
            1,
            Math.ceil((visibleEnd.getTime() - visibleStart.getTime()) / (SLOT_INTERVAL * 60000))
          );

          for (let i = 0; i < rowsToSpan; i++) {
            covered[`${day}-${row + i}`] = true;
          }

          const isConge = !!ev.isConge;
          const baseClass = isConge ? "slot-conge" : "slot booked";
          const idKey = getKey(ev);
          const rdvBg = ev.isRdv ? rdvColors[idKey] : undefined;

          slotElements.push(
            <div
              key={`ev-${day}-${hour}-${idKey}`}
              className={`${baseClass} ${ev.isRdv ? "slot-rdv" : ""}`}
              style={{
                gridRow: `${row} / span ${rowsToSpan}`,
                gridColumn: day + 2,
                zIndex: isConge ? 3 : 4,
                ...(rdvBg ? { background: rdvBg, color: "#fff" } : {}),
              }}
              onClick={() => handleSlotClick({ ...ev })}
              title={isConge ? (ev.reason ? `Congé: ${ev.reason}` : "Congé") : ev.title}
            >
              <div className="slot-info">
                <div className="slot-time" style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
                  <span>
                    {new Date(ev.start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}{" "}
                    —{" "}
                    {ev.end
                      ? new Date(ev.end).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                      : ""}
                  </span>
                </div>
                {!isConge && <div><span style={{ fontWeight: 500 }}>{ev.title || ""}</span></div>}
                {isConge && <div><span style={{ fontWeight: 500 }}>{ev.reason ? `Congé — ${ev.reason}` : "Congé"}</span></div>}
              </div>
            </div>
          );
        } else {
          // Libre : on vérifie si ce créneau est à l'intérieur des horaires du profil
          const allowed = isInsideWorkHours(cellStart, workHours);
          slotElements.push(
            <div
              key={`free-${day}-${hour}`}
              className={`slot ${isCongeMode || isCongeRangeMode ? "slot-conge-mode" : ""} ${allowed ? "" : "slot-closed"}`}
              style={{
                gridRow: row,
                gridColumn: day + 2,
                ...(allowed ? {} : { background: "#f3f4f6", cursor: "not-allowed", opacity: 0.65 }),
              }}
              onClick={() => allowed && handleSelection(day, hour, 0)}
              title={allowed ? "" : "Hors horaires du profil"}
            />
          );
        }
      }
    }

    // Colonne heures
    for (let hour = START_HOUR; hour <= END_HOUR; hour++) {
      slotElements.push(
        <div
          key={`hour-cell-${hour}`}
          className="hour-cell"
          style={{ gridRow: hour - START_HOUR + 1, gridColumn: 1 }}
        >
          {hour}h00
        </div>
      );
    }
    return slotElements;
  };

  // ====== Version App : vue JOUR + onglets ======
  const firstDayOfWeek = getMonday(currentDate);
  const appSelectedDate = (() => {
    const d = new Date(firstDayOfWeek);
    d.setDate(firstDayOfWeek.getDate() + appSelectedDayIdx);
    return d;
  })();

  const appRenderDayTabs = () => {
    const today = new Date();
    return (
      <div className="app-day-tabs">
        {daysLabels.map((lbl, i) => {
          const d = new Date(firstDayOfWeek);
          d.setDate(firstDayOfWeek.getDate() + i);
          const isToday =
            d.getDate() === today.getDate() &&
            d.getMonth() === today.getMonth() &&
            d.getFullYear() === today.getFullYear();

          return (
            <button
              key={lbl}
              className={`app-day-tab ${appSelectedDayIdx === i ? "active" : ""} ${isToday ? "today" : ""}`}
              onClick={() => setAppSelectedDayIdx(i)}
            >
              <span className="d">{lbl}</span>
              <span className="n">{d.toLocaleDateString("fr-FR", { day: "2-digit" })}</span>
            </button>
          );
        })}
      </div>
    );
  };

  const appRenderDaySlots = () => {
    // filtre les events du jour sélectionné
    const startDay = new Date(appSelectedDate);
    startDay.setHours(0,0,0,0);
    const endDay = new Date(appSelectedDate);
    endDay.setHours(23,59,59,999);

    const events = [...slots.map((s) => ({ ...s, isConge: false })), ...conges]
      .filter((e) => {
        const s = new Date(e.start);
        const ed = new Date(e.end);
        return ed > startDay && s < endDay;
      })
      .sort((a,b)=> new Date(a.start) - new Date(b.start));

    const getKey = (ev) => ev._id || ev.id || (new Date(ev.start).toISOString() + (ev.title || ""));

    const rows = [];
    for (let hour = START_HOUR; hour <= END_HOUR; hour++) {
      rows.push(
        <div key={`h-${hour}`} className="app-hour-row">
          <div className="app-hour">{`${String(hour).padStart(2,"0")}:00`}</div>
          <div className="app-hour-slots">
            {/* zone cliquable pour créer un slot à cette heure (si autorisé) */}
            <div
              className={`app-free-lane ${isCongeMode ? "conge-mode" : ""}`}
              onClick={() => {
                const d = new Date(appSelectedDate);
                d.setHours(hour, 0, 0, 0);
                if (isInsideWorkHours(d, workHours)) {
                  handleSelection(appSelectedDayIdx, hour, 0);
                }
              }}
              title={(() => {
                const d = new Date(appSelectedDate);
                d.setHours(hour, 0, 0, 0);
                return isInsideWorkHours(d, workHours) ? "" : "Hors horaires du profil";
              })()}
              style={(() => {
                const d = new Date(appSelectedDate);
                d.setHours(hour, 0, 0, 0);
                const allowed = isInsideWorkHours(d, workHours);
                return allowed ? {} : { background: "#f3f4f6", cursor: "not-allowed", opacity: 0.65 };
              })()}
            />
            {/* events qui commencent sur cette heure */}
            {events
              .filter((ev)=>{
                const s = new Date(ev.start);
                return s.getHours() === hour;
              })
              .map((ev)=>{
                const start = new Date(ev.start);
                const end = new Date(ev.end);
                const minutes = Math.max(30, (end - start) / 60000);
                const height = Math.max(44, (minutes / 60) * 72); // 72px ~ hauteur 1h
                const color = ev.isRdv ? rdvColors[getKey(ev)] : undefined;
                return (
                  <button
                    key={ev._id || ev.id || start.toISOString()}
                    className={`app-event ${ev.isConge ? "is-conge" : ev.isRdv ? "is-rdv" : ""}`}
                    style={{ height, ...(color ? { background: color, color: "#fff" } : {}) }}
                    onClick={() => handleSlotClick(ev)}
                  >
                    <div className="t">{start.toLocaleTimeString([], {hour:"2-digit", minute:"2-digit"})} — {end.toLocaleTimeString([], {hour:"2-digit", minute:"2-digit"})}</div>
                    <div className="tt">{ev.isConge ? (ev.reason || "Congé") : (ev.title || "Créneau")}</div>
                  </button>
                );
              })}
          </div>
        </div>
      );
    }
    return <div className="app-day-grid">{rows}</div>;
  };

  // ------- Popups -------
  const renderSlotDetailsPopup = () => {
    if (!popupSlotDetails) return null;
    const ev = popupSlotDetails;
    const isConge = !!ev.isConge;

    return (
      <div
        className="popup-overlay"
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.13)",
          zIndex: 2222,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          className="popup-slot-modal"
          style={{
            minWidth: 320,
            maxWidth: 420,
            padding: 30,
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 8px 30px #9d857330",
            position: "relative",
          }}
        >
          <button
            onClick={() => setPopupSlotDetails(null)}
            style={{
              position: "absolute",
              top: 11,
              right: 15,
              background: "none",
              border: "none",
              fontSize: 22,
              fontWeight: 700,
              color: "#b63838",
              cursor: "pointer",
            }}
            aria-label="Fermer"
          >
            ×
          </button>

          <div style={{ marginBottom: 18, fontWeight: 700, fontSize: 18, color: "#a05c41" }}>
            {isConge ? "Détails du congé" : "Détails du créneau"}
          </div>

          {/* ⚠️ Bandeau d’information (uniquement pour un créneau, pas pour un congé) */}
          {!isConge && (
            <div
              style={{
                marginBottom: 14,
                padding: "10px 12px",
                borderRadius: 10,
                background: "#fee2e2",
                border: "1px solid #ef4444",
                color: "#991b1b",
                fontSize: 14,
                lineHeight: 1.35,
                fontWeight: 600,
              }}
            >
              ⚠️ <span style={{ textDecoration: "underline" }}>Annulation</span> :
              si vous souhaitez annuler ce créneau, merci de prévenir le client au plus vite
              (appel / SMS / e-mail). Pensez également à proposer une nouvelle
              disponibilité.
            </div>
          )}

          <div style={{ marginBottom: 9 }}>
            <strong>Début :</strong> {ev.start && new Date(ev.start).toLocaleString("fr-FR")}
          </div>
          <div style={{ marginBottom: 9 }}>
            <strong>Fin :</strong> {ev.end && new Date(ev.end).toLocaleString("fr-FR")}
          </div>

          {!isConge && ev.title && (
            <div style={{ marginBottom: 7 }}>
              <strong>Titre :</strong> {ev.title}
            </div>
          )}

          {!isConge && ev.description && (
            <div style={{ marginBottom: 7, whiteSpace: "pre-line" }}>
              <strong>Description :</strong>
              <br />
              {ev.description}
            </div>
          )}

          {isConge && ev.reason && (
            <div style={{ marginBottom: 7, whiteSpace: "pre-line" }}>
              <strong>Raison :</strong>
              <br />
              {ev.reason}
            </div>
          )}

          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            {isConge && (ev._id || ev.id) && (
              <button
                onClick={() => {
                  setCongeDraft({
                    _id: ev._id || ev.id,
                    allDay: true,
                    start: new Date(ev.start),
                    end: new Date(ev.end),
                    duration: 60,
                    reason: ev.reason || "",
                  });
                  setPopupCongeVisible(true);
                  setPopupSlotDetails(null);
                }}
                style={{
                  padding: "10px 16px",
                  borderRadius: 10,
                  border: "none",
                  background: "#8b5cf6",
                  color: "#fff",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Modifier
              </button>
            )}

            {(ev._id || ev.id) && (
              <button
                onClick={() => deleteSlot(ev._id || ev.id)}
                style={{
                  padding: "10px 16px",
                  background: "linear-gradient(90deg,#d63434 0%,#bb9c8d 100%)",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 15,
                  border: "none",
                  borderRadius: 10,
                  boxShadow: "0 2px 10px #db847012",
                  cursor: "pointer",
                }}
              >
                {isConge ? "Supprimer ce congé" : "Supprimer ce créneau"}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCongePopup = () => {
    if (!popupCongeVisible) return null;
    const start = congeDraft.start ? new Date(congeDraft.start) : new Date();
    const isEditing = !!congeDraft._id;
    const isRange =
      !!(congeDraft.end && congeDraft.start && congeDraft.end - congeDraft.start > 60 * 60 * 1000);

    return (
      <div className="popup-overlay" style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.13)", zIndex:3000, display:"flex", alignItems:"center", justifyContent:"center" }}>
        <div style={{ width:440, maxWidth:"96vw", background:"#fff", borderRadius:16, padding:22, boxShadow:"0 10px 30px #0001", position:"relative" }}>
          <button onClick={() => setPopupCongeVisible(false)} style={{ position:"absolute", top:10, right:12, background:"none", border:"none", fontSize:22, cursor:"pointer", color:"#b63838" }}>×</button>
          <h3 style={{ margin:0, marginBottom:12, color:"#8a5a3f" }}>
            {isEditing ? "Modifier un congé" : isRange ? "Ajouter une période de congé" : "Ajouter un congé"}
          </h3>

          <div style={{ marginBottom:10 }}>
            <label style={{ display:"flex", alignItems:"center", gap:8 }}>
              <input
                type="checkbox"
                checked={congeDraft.allDay}
                onChange={(e) => setCongeDraft((prev) => ({ ...prev, allDay: e.target.checked }))}
                disabled={!!miniRange}
              />
              <span>Période de congés</span>
            </label>
          </div>

          <div style={{ display:"flex", gap:10, marginBottom:8 }}>
            <div style={{ flex:1 }}>
              <label style={{ display:"block", fontSize:13, marginBottom:4 }}>
                {congeDraft.allDay ? "Date (début)" : "Début"}
              </label>
              <input
                type={congeDraft.allDay ? "date" : "datetime-local"}
                value={
                  congeDraft.allDay
                    ? (() => {
                        const y = start.getFullYear();
                        const m = String(start.getMonth() + 1).padStart(2, "0");
                        const d = String(start.getDate()).padStart(2, "0");
                        return `${y}-${m}-${d}`;
                      })()
                    : (() => {
                        const y = start.getFullYear();
                        const m = String(start.getMonth() + 1).padStart(2, "0");
                        const d = String(start.getDate()).padStart(2, "0");
                        const hh = String(start.getHours()).padStart(2, "0");
                        const mm = String(start.getMinutes()).padStart(2, "0");
                        return `${y}-${m}-${d}T${hh}:${mm}`;
                      })()
                }
                onChange={(e) => {
                  const val = e.target.value;
                  if (!val) return;
                  let dt;
                  if (congeDraft.allDay) {
                    const [y, m, d] = safeSplit(val, "-");
                    dt = new Date(safeParseInt(y), safeParseInt(m) - 1, safeParseInt(d), 0, 0, 0, 0);
                  } else {
                    dt = new Date(val);
                  }
                  setCongeDraft((prev) => ({ ...prev, start: dt }));
                }}
              />
            </div>

            {!congeDraft.allDay && !miniRange && (
              <div style={{ width: 160 }}>
                <label style={{ display:"block", fontSize:13, marginBottom:4 }}>Durée (min)</label>
                <input
                  type="number"
                  min={15}
                  step={15}
                  value={congeDraft.duration}
                  onChange={(e) => setCongeDraft((prev) => ({ ...prev, duration: safeParseInt(e.target.value || "60", 60) }))}
                  style={{ width:"100%" }}
                />
              </div>
            )}
          </div>

          {(congeDraft.allDay || miniRange) && (
            <div style={{ marginBottom: 8 }}>
              <label style={{ display:"block", fontSize:13, marginBottom:4 }}>Date (fin)</label>
              <input
                type="date"
                value={(() => {
                  const end = congeDraft.end ? new Date(congeDraft.end) : new Date(congeDraft.start || new Date());
                  const y = end.getFullYear();
                  const m = String(end.getMonth() + 1).padStart(2, "0");
                  const d = String(end.getDate()).padStart(2, "0");
                  return `${y}-${m}-${d}`;
                })()}
                onChange={(e) => {
                  const [y, m, d] = safeSplit(e.target.value, "-");
                  const end = new Date(safeParseInt(y), safeParseInt(m) - 1, safeParseInt(d), 23, 59, 59, 999);
                  setCongeDraft((prev) => ({ ...prev, end }));
                }}
              />
              <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>La fin est incluse (23:59).</div>
            </div>
          )}

          <div style={{ marginTop: 8 }}>
            <label style={{ display:"block", fontSize:13, marginBottom:4 }}>Raison (optionnel)</label>
            <input
              type="text"
              placeholder="Ex: Vacances, fermeture exceptionnelle..."
              value={congeDraft.reason}
              onChange={(e) => setCongeDraft((prev) => ({ ...prev, reason: e.target.value }))}
              style={{ width: "100%" }}
            />
          </div>

          <div style={{ display:"flex", gap:10, justifyContent:"space-between", marginTop:16 }}>
            <div style={{ display:"flex", gap:8 }}>
              <button
                onClick={() => { setPopupCongeVisible(false); setMiniRange(null); setIsCongeRangeMode(false); }}
                style={{ padding:"8px 16px", borderRadius:10, border:"1px solid #ddd", background:"#f8f8f8", cursor:"pointer" }}
              >
                Annuler
              </button>
              {congeDraft._id && (
                <button
                  onClick={() => { setPopupCongeVisible(false); deleteConge(congeDraft._id); }}
                  style={{ padding:"8px 16px", borderRadius:10, border:"none", background:"#dc2626", color:"#fff", fontWeight:700, cursor:"pointer" }}
                >
                  Supprimer
                </button>
              )}
            </div>
            <button
              onClick={saveConge}
              style={{ padding:"8px 16px", borderRadius:10, border:"none", background:"#2563eb", color:"#fff", fontWeight:700, cursor:"pointer" }}
            >
              {congeDraft._id ? "Enregistrer les modifications" : "Enregistrer le congé"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ----------- RENDER FINAL -----------
  if (isNativeApp) {
    // ====== VERSION APP (vue jour + onglets + FAB) ======
    return (
      <div className="calendar-app-container">
        <div className="app-week-header">
          <button className="nav" onClick={() => navigateWeek(-1)}>◀</button>
          <div className="title">
            Semaine du{" "}
            {firstDayOfWeek.toLocaleDateString("fr-FR", { day:"2-digit", month:"2-digit" })}
          </div>
        </div>

        {appRenderDayTabs()}

        <div className="app-toolbar">
          <button
            className={`pill ${isCongeMode ? "active" : ""}`}
            onClick={() => setIsCongeMode((v) => !v)}
          >
            {isCongeMode ? "Mode Congé ✅" : "Mode Congé"}
          </button>
          <button className="pill" onClick={() => setCurrentDate(getMonday(new Date()))}>
            Aujourd’hui
          </button>
        </div>

        {appRenderDaySlots()}

        {/* FAB pour créer rapidement à l’heure actuelle */}
        <button
          className="app-fab"
          onClick={() => {
            const now = new Date();
            const idx = (now.getDay() + 6) % 7;
            setAppSelectedDayIdx(idx);
            const d = new Date(now);
            d.setSeconds(0,0);
            if (isInsideWorkHours(d, workHours)) {
              handleSelection(idx, now.getHours(), now.getMinutes());
            }
          }}
          aria-label="Ajouter un créneau"
          title="Ajouter un créneau"
        >
          +
        </button>

        {/* POPUPS */}
        <PopupSlot
          visible={popupVisible}
          onClose={() => setPopupVisible(false)}
          onSave={saveSlot}
          slotTime={selectedSlotTime || new Date()}
          annonceId={annonceId}
        />
        {renderSlotDetailsPopup()}
        {renderCongePopup()}
      </div>
    );
  }

  // ====== VERSION WEB (vue semaine) ======
  return (
    <div className="calendar-container">
      <div className="calendar-mini-side">
        <div className="conge-controls">
          <button
            className={`calandar-container-button ${isCongeMode ? "active" : ""}`}
            onClick={() => setIsCongeMode((v) => !v)}
            title="Ajout de congé en cliquant une case horaire"
          >
            {isCongeMode ? "✅ Mode congé" : "➕ Mode congé"}
          </button>
        </div>

        <CalendarMini
          className="mini-cal"
          value={selectedDate}
          onChange={handleMiniCalendarChange}
          locale="fr-FR"
        />

        {/* 🆕 Récap des horaires du profil */}
        {renderWorkHoursSummary()}
      </div>

      <div className="calendar-content">
        <div className="calandar-container-button-group">
          <button className="calandar-container-button" onClick={() => navigateWeek(-1)}>
            Précédent
          </button>
          <button className="calandar-container-button" onClick={() => navigateWeek(1)}>
            Suivant
          </button>
        </div>

        {/* 🆕 Mois + Année (bas des boutons) */}
        <div
          className="calendar-month-year"
          style={{
            textAlign: "center",
            fontWeight: 800,
            fontSize: 22,
            color: "#8a5a3f",
            margin: "8px 0 14px",
          }}
        >
          {formatMonthYear(firstDayOfWeek)}
        </div>

        <div className="calendar">
          <div className="days-row">
            <div className="hour-column-header"></div>
            {renderDays()}
          </div>
          <div className="slots-grid">{renderSlots()}</div>
        </div>
      </div>

      {/* POPUP AJOUT (case blanche) */}
      <PopupSlot
        visible={popupVisible}
        onClose={() => setPopupVisible(false)}
        onSave={saveSlot}
        slotTime={selectedSlotTime || new Date()}
        annonceId={annonceId}
      />

      {/* POPUP DÉTAIL (case colorée) */}
      {renderSlotDetailsPopup()}

      {/* POPUP CONGÉ (création/édition) */}
      {renderCongePopup()}
    </div>
  );
};

export default Calendar;
